import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtMXN, fmtMXNFull } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import ProjectTabs from "@/components/ProjectTabs";
import { useAuth } from "@/context/AuthContext";
import { Upload, Download, FileDown, Trash2, ArrowLeft, AlertTriangle, BarChart3, Smartphone } from "lucide-react";
import { toast } from "sonner";

export default function ProjectBudget() {
  const { id: projectId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [data, setData] = useState({ items: [], totals: {} });
  const [analysis, setAnalysis] = useState(null);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const canManage = ["admin", "contador"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const [p, b] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/budget`),
      ]);
      setProject(p.data);
      setData(b.data);
      // Cargar análisis solo si hay partidas con ejecución (ahorra petición innecesaria)
      const hasExec = (b.data?.totals?.total_executed || 0) > 0;
      if (hasExec) {
        try {
          const { data: an } = await api.get(`/projects/${projectId}/budget/analysis`);
          setAnalysis(an);
        } catch {
          setAnalysis(null);
        }
      } else {
        setAnalysis(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cargar");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const handleImport = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (data.items?.length > 0) {
      if (!window.confirm(`Este proyecto ya tiene ${data.items.length} partidas. ¿Reemplazar TODO el presupuesto con el nuevo archivo? (los vínculos de gastos a partidas se perderán)`)) {
        e.target.value = "";
        return;
      }
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data: res } = await api.post(`/projects/${projectId}/budget/import?replace=true`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Importadas ${res.imported} líneas (${res.groups} grupos · ${res.chapters} capítulos · ${res.items} partidas)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const clearAll = async () => {
    if (!window.confirm("¿Eliminar TODO el presupuesto del proyecto? Esta acción no se puede deshacer.")) return;
    try {
      await api.delete(`/projects/${projectId}/budget`);
      toast.success("Presupuesto eliminado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const downloadPDF = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/budget/pdf`, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      link.download = `presupuesto-${project?.code || "obra"}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      toast.error("Error al generar PDF");
    }
  };
  const downloadExcel = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/budget/excel`, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      link.download = `presupuesto-${project?.code || "obra"}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      toast.error("Error al generar Excel");
    }
  };

  const saveProgress = async (itemId, value) => {
    // value puede ser número 0-200 o null para limpiar
    try {
      const payload = value === null || value === "" ? { progress_pct_manual: 0 } : { progress_pct_manual: Number(value) };
      await api.patch(`/projects/${projectId}/budget/${itemId}`, payload);
      // Optimistic update local
      setData((prev) => {
        const next = { ...prev, items: prev.items.map((it) => it.id === itemId ? { ...it, progress_pct_manual: payload.progress_pct_manual } : it) };
        // Recalcular avance físico ponderado
        let wp = 0, wt = 0, count = 0;
        for (const it of next.items) {
          if (it.level === "item" && it.progress_pct_manual != null && (it.amount || 0) > 0) {
            wp += it.amount * Number(it.progress_pct_manual);
            wt += it.amount;
            count++;
          }
        }
        next.totals = { ...prev.totals, physical_progress_pct: wt > 0 ? Math.round((wp / wt) * 100) / 100 : 0, items_with_progress: count };
        return next;
      });
      toast.success("Avance actualizado", { duration: 1200 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar avance");
    }
  };

  const totals = data.totals || {};
  const overBudget = (totals.progress_pct || 0) > 100;

  return (
    <div data-testid="budget-page">
      <PageHeader
        subtitle={project ? `${project.code || ""} · ${project.name || ""}` : "Cargando..."}
        title="Presupuesto de obra"
        actions={
          <div className="flex gap-2 flex-wrap">
            <button onClick={() => nav(`/projects/${projectId}/report`)} className="btn-ghost px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest">
              <ArrowLeft className="w-4 h-4" /> Reporte de obra
            </button>
            <button
              data-testid="budget-mobile-progress-btn"
              onClick={() => nav(`/projects/${projectId}/progress-mobile`)}
              className="px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold"
              style={{ background: "#7c3aed", color: "white", boxShadow: "0 0 12px rgba(124,58,237,0.4)" }}
              title="Vista móvil para capturar avance desde el celular"
            >
              <Smartphone className="w-4 h-4" /> Capturar avance (móvil)
            </button>
            <button data-testid="budget-pdf-btn" onClick={downloadPDF} className="btn-ghost px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest"
              style={{ borderColor: "var(--magenta)", color: "var(--magenta)" }}>
              <FileDown className="w-4 h-4" /> PDF
            </button>
            {["admin", "contador"].includes(user?.role) && (
              <button data-testid="budget-excel-btn" onClick={downloadExcel} className="btn-ghost px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest"
                style={{ borderColor: "var(--green, #16a34a)", color: "var(--green, #16a34a)" }}>
                <Download className="w-4 h-4" /> Excel
              </button>
            )}
            {canManage && (
              <>
                <input ref={fileRef} type="file" accept=".xls,.xlsx,.xlsm" onChange={handleImport} className="hidden" data-testid="budget-import-input" />
                <button data-testid="budget-import-btn" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="btn-primary px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest">
                  <Upload className="w-4 h-4" /> {uploading ? "Importando..." : "Importar Excel"}
                </button>
                {data.items?.length > 0 && (
                  <button data-testid="budget-clear-btn" onClick={clearAll} className="btn-ghost px-3 py-2 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest"
                    style={{ borderColor: "var(--red, #dc2626)", color: "var(--red, #dc2626)" }}>
                    <Trash2 className="w-4 h-4" /> Vaciar
                  </button>
                )}
              </>
            )}
          </div>
        }
      />

      <div className="px-4 lg:px-8 py-6 space-y-4">
        <ProjectTabs projectId={projectId} active="budget" />
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Kpi label="Total presupuesto" value={totals.total_budget} color="#3F2A91" />
          <Kpi label="Ejecutado $" value={totals.total_executed} color="#d97706" />
          <Kpi label="Restante" value={totals.total_remaining} color={overBudget ? "#dc2626" : "#16a34a"} />
          <Kpi label="% Económico" value={`${(totals.progress_pct || 0).toFixed(1)}%`} color={overBudget ? "#dc2626" : "var(--cyan)"} isPct />
          <Kpi label="% Avance Físico" value={`${(totals.physical_progress_pct || 0).toFixed(1)}%`} color="#7c3aed" isPct />
          <Kpi label="Partidas" value={`${totals.items_with_progress || 0} / ${totals.item_count || 0}`} color="var(--cyan)" raw />
        </div>

        {overBudget && (
          <div className="panel p-3 flex items-center gap-2 text-sm" style={{ background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.4)", color: "#fca5a5" }}>
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <div>El gasto ejecutado supera el presupuesto total ({(totals.progress_pct || 0).toFixed(1)}%).</div>
          </div>
        )}

        {/* Análisis presupuestal — solo si hay ejecución */}
        {analysis && (
          <div className="panel">
            <button
              onClick={() => setShowAnalysis((s) => !s)}
              data-testid="toggle-analysis-btn"
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/5"
              style={{ borderBottom: showAnalysis ? "1px solid var(--border)" : "none" }}
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" style={{ color: "#7c3aed" }} />
                <span className="font-semibold text-sm">Análisis Presupuestal</span>
                <span className="label-mono text-[10px] text-slate-500">
                  {analysis.over_count} sobreejecuciones · {analysis.untouched_count} sin gasto · {analysis.by_chapter?.length || 0} capítulos
                </span>
              </div>
              <span className="text-xs text-slate-400">{showAnalysis ? "Ocultar ▲" : "Ver ▼"}</span>
            </button>
            {showAnalysis && <AnalysisPanel analysis={analysis} />}
          </div>
        )}

        {/* Tabla */}
        <div className="panel overflow-x-auto">
          {loading ? (
            <div className="text-center py-12 text-slate-500">Cargando...</div>
          ) : data.items?.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <div className="mb-3">Sin presupuesto cargado todavía.</div>
              {canManage && <div className="text-xs">Usa el botón <b>Importar Excel</b> para cargar el catálogo de partidas (.xls o .xlsx).</div>}
            </div>
          ) : (
            <table className="w-full text-sm min-w-[1100px]" data-testid="budget-table">
              <thead className="panel-header">
                <tr className="text-left">
                  <Th>Código</Th>
                  <Th>Concepto</Th>
                  <Th>Unidad</Th>
                  <Th right>Cantidad</Th>
                  <Th right>P. Unitario</Th>
                  <Th right>Importe</Th>
                  <Th right>Ejecutado</Th>
                  <Th right>Restante</Th>
                  <Th right>% Econ.</Th>
                  <Th right>% Físico</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it) => {
                  const isGroup = it.level === "group";
                  const isChapter = it.level === "chapter";
                  const isItem = it.level === "item";
                  const overPart = isItem && (it.amount || 0) > 0 && (it.executed || 0) > (it.amount || 0);
                  return (
                    <tr key={it.id}
                      data-testid={`budget-row-${it.id}`}
                      style={{
                        background: isGroup ? "rgba(124,58,237,0.18)" : isChapter ? "rgba(124,58,237,0.07)" : undefined,
                        borderBottom: "1px solid var(--border)",
                      }}>
                      <td className="px-3 py-2 font-mono text-xs">{it.code || ""}</td>
                      <td className={`px-3 py-2 ${isGroup || isChapter ? "font-bold" : ""}`} style={{ paddingLeft: isGroup ? 12 : isChapter ? 24 : 36 }}>
                        {it.description}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">{it.unit || ""}</td>
                      <td className="px-3 py-2 text-right font-mono">{it.quantity != null ? Number(it.quantity).toLocaleString("en-US", { maximumFractionDigits: 2 }) : ""}</td>
                      <td className="px-3 py-2 text-right font-mono">{it.unit_price != null ? fmtMXN(it.unit_price) : ""}</td>
                      <td className="px-3 py-2 text-right font-mono font-semibold">{it.amount != null ? fmtMXN(it.amount) : ""}</td>
                      <td className="px-3 py-2 text-right font-mono" style={{ color: "#d97706" }}>{isItem ? fmtMXN(it.executed || 0) : ""}</td>
                      <td className="px-3 py-2 text-right font-mono" style={{ color: overPart ? "#dc2626" : "#16a34a" }}>{isItem ? fmtMXN(it.remaining || 0) : ""}</td>
                      <td className="px-3 py-2 text-right font-mono" style={{ color: overPart ? "#dc2626" : "var(--cyan)" }}>
                        {isItem && (it.amount || 0) > 0 ? `${(it.progress_pct || 0).toFixed(1)}%` : ""}
                      </td>
                      <td className="px-2 py-1 text-right">
                        {isItem ? (
                          <ProgressInput
                            value={it.progress_pct_manual}
                            disabled={!canManage}
                            onSave={(val) => saveProgress(it.id, val)}
                            testId={`progress-${it.id}`}
                          />
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="panel-header">
                <tr>
                  <td colSpan={5} className="px-3 py-3 text-right text-xs uppercase tracking-widest font-bold">TOTAL</td>
                  <td className="px-3 py-3 text-right font-mono font-bold">{fmtMXNFull(totals.total_budget || 0)}</td>
                  <td className="px-3 py-3 text-right font-mono font-bold" style={{ color: "#d97706" }}>{fmtMXNFull(totals.total_executed || 0)}</td>
                  <td className="px-3 py-3 text-right font-mono font-bold" style={{ color: overBudget ? "#dc2626" : "#16a34a" }}>{fmtMXNFull(totals.total_remaining || 0)}</td>
                  <td className="px-3 py-3 text-right font-mono font-bold" style={{ color: overBudget ? "#dc2626" : "var(--cyan)" }}>{(totals.progress_pct || 0).toFixed(1)}%</td>
                  <td className="px-3 py-3 text-right font-mono font-bold" style={{ color: "#7c3aed" }}>{(totals.physical_progress_pct || 0).toFixed(1)}%</td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const Th = ({ children, right }) => (
  <th className={`label-mono px-3 py-3 ${right ? "text-right" : ""}`}>{children}</th>
);

const Kpi = ({ label, value, color, isPct, raw }) => (
  <div className="panel p-3">
    <div className="label-mono text-[10px] mb-1" style={{ color }}>{label}</div>
    <div className="font-mono text-lg font-bold text-slate-100">
      {isPct || raw ? value : fmtMXNFull(value || 0)}
    </div>
  </div>
);

function ProgressInput({ value, disabled, onSave, testId }) {
  const [local, setLocal] = useState(value != null ? String(value) : "");
  const [dirty, setDirty] = useState(false);

  // Sync from prop when external value changes (e.g., after reload)
  useEffect(() => { if (!dirty) setLocal(value != null ? String(value) : ""); /* eslint-disable-next-line */ }, [value]);

  const commit = () => {
    if (!dirty) return;
    const v = local.trim();
    if (v === "") {
      onSave(null);
    } else {
      const n = Number(v);
      if (Number.isNaN(n) || n < 0 || n > 200) {
        toast.error("Avance debe estar entre 0 y 200%");
        setLocal(value != null ? String(value) : "");
        setDirty(false);
        return;
      }
      onSave(n);
    }
    setDirty(false);
  };

  const num = value != null ? Number(value) : null;
  const color = num == null ? "#64748b" : num >= 100 ? "#16a34a" : num >= 75 ? "#22c55e" : num >= 50 ? "#0891b2" : num >= 25 ? "#d97706" : "#94a3b8";

  return (
    <div className="flex items-center justify-end gap-1">
      <input
        type="number"
        step="1"
        min={0}
        max={200}
        disabled={disabled}
        value={local}
        onChange={(e) => { setLocal(e.target.value); setDirty(true); }}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") { e.target.blur(); } if (e.key === "Escape") { setLocal(value != null ? String(value) : ""); setDirty(false); e.target.blur(); } }}
        placeholder="—"
        data-testid={testId}
        className="w-14 text-right font-mono text-xs px-1 py-0.5 rounded-sm bg-transparent border focus:outline-none"
        style={{
          borderColor: dirty ? "#7c3aed" : "var(--border)",
          color,
          background: dirty ? "rgba(124,58,237,0.06)" : undefined,
        }}
      />
      <span className="font-mono text-xs" style={{ color }}>%</span>
    </div>
  );
}

function AnalysisPanel({ analysis }) {
  const chapters = analysis.by_chapter || [];
  const overItems = analysis.over_budget_items || [];
  const untouched = analysis.untouched_items || [];
  const maxBudget = Math.max(...chapters.map((c) => c.budget || 0), 1);

  return (
    <div className="p-4 space-y-5">
      {/* Gráfica horizontal de barras: Presupuesto vs Ejecutado por capítulo */}
      <div>
        <div className="label-mono mb-3" style={{ color: "#7c3aed" }}>// PRESUPUESTO VS EJECUTADO POR CAPÍTULO</div>
        <div className="space-y-2">
          {chapters.length === 0 && <div className="text-xs text-slate-500 text-center py-4">Sin capítulos con gasto vinculado.</div>}
          {chapters.map((c) => {
            const budgetW = (c.budget / maxBudget) * 100;
            const execW = (c.executed / maxBudget) * 100;
            const overChapter = c.progress_pct > 100;
            return (
              <div key={c.chapter_id} className="text-xs" data-testid={`analysis-chapter-${c.code}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold">
                    <span className="font-mono text-slate-500 mr-2">{c.code}</span>
                    {c.description}
                  </span>
                  <span className="font-mono" style={{ color: overChapter ? "#dc2626" : "var(--cyan)" }}>
                    {c.progress_pct.toFixed(1)}%
                  </span>
                </div>
                <div className="relative h-5 rounded-sm overflow-hidden" style={{ background: "rgba(255,255,255,0.04)" }}>
                  {/* Barra presupuesto (fondo morado claro) */}
                  <div className="absolute inset-y-0 left-0" style={{ width: `${budgetW}%`, background: "rgba(124,58,237,0.25)" }} />
                  {/* Barra ejecutado (encima) */}
                  <div className="absolute inset-y-0 left-0" style={{
                    width: `${Math.min(100, execW)}%`,
                    background: overChapter ? "linear-gradient(90deg, #d97706, #dc2626)" : "linear-gradient(90deg, #16a34a, #0891b2)",
                  }} />
                  <div className="absolute inset-0 flex items-center justify-between px-2 font-mono text-[10px] text-slate-200">
                    <span>{fmtMXN(c.executed || 0)}</span>
                    <span className="text-slate-400">/ {fmtMXN(c.budget || 0)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top sobreejecuciones */}
      {overItems.length > 0 && (
        <div>
          <div className="label-mono mb-2" style={{ color: "#dc2626" }}>// TOP PARTIDAS CON SOBREEJECUCIÓN ({analysis.over_count})</div>
          <table className="w-full text-xs" data-testid="analysis-over-table">
            <thead className="panel-header">
              <tr className="text-left">
                <th className="label-mono px-3 py-2">Código</th>
                <th className="label-mono px-3 py-2">Concepto</th>
                <th className="label-mono px-3 py-2 text-right">Presupuesto</th>
                <th className="label-mono px-3 py-2 text-right">Ejecutado</th>
                <th className="label-mono px-3 py-2 text-right">Excedente</th>
                <th className="label-mono px-3 py-2 text-right">%</th>
              </tr>
            </thead>
            <tbody>
              {overItems.map((it) => (
                <tr key={it.id} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td className="px-3 py-2 font-mono">{it.code}</td>
                  <td className="px-3 py-2">{(it.description || "").slice(0, 80)}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtMXN(it.budget)}</td>
                  <td className="px-3 py-2 text-right font-mono" style={{ color: "#d97706" }}>{fmtMXN(it.executed)}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold" style={{ color: "#dc2626" }}>+{fmtMXN(it.over_amount)}</td>
                  <td className="px-3 py-2 text-right font-mono" style={{ color: "#dc2626" }}>{it.progress_pct.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Partidas sin gasto */}
      {untouched.length > 0 && (
        <div>
          <div className="label-mono mb-2" style={{ color: "#94a3b8" }}>// PARTIDAS SIN GASTO VINCULADO (TOP 10 DE {analysis.untouched_count} · ORDENADAS POR PRESUPUESTO)</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs" data-testid="analysis-untouched">
            {untouched.map((it) => (
              <div key={it.id} className="panel p-2 flex items-center justify-between" style={{ background: "rgba(8,12,24,0.4)" }}>
                <div className="flex-1 min-w-0">
                  <span className="font-mono text-slate-500 mr-2">{it.code}</span>
                  <span className="text-slate-200 truncate">{(it.description || "").slice(0, 60)}</span>
                </div>
                <div className="font-mono text-slate-400 flex-shrink-0 ml-2">{fmtMXN(it.budget)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
